import { AvatarPanel } from '@/components/AvatarPanel';
import { ZONE_COLOR } from '@/components/AvatarMaskOverlay';
import { summarizeFit } from '@/utils/fitSummary';
import type { Audience, ComparisonResult } from '@/types/domain';

interface Props {
  audience: Audience;
  result: ComparisonResult;
  selected: boolean;
  onSelect: () => void;
  isBestMatch: boolean;
}

function StarIcon() {
  return (
    <svg viewBox="0 0 20 20" aria-hidden="true" className="h-3.5 w-3.5 shrink-0 fill-current">
      <path d="M10 2.8 12.2 7.6 17.4 8.3 13.7 12 14.6 17.2 10 14.7 5.4 17.2 6.3 12 2.6 8.3 7.8 7.6Z" />
    </svg>
  );
}

function ChevronIcon() {
  return (
    <svg viewBox="0 0 20 20" aria-hidden="true" className="h-5 w-5 shrink-0 text-muted">
      <path d="M7.5 4.5 13 10l-5.5 5.5" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/**
 * The "Your Fit Matches" list card — a restyled ResultCard with a compact
 * per-result avatar thumbnail (reusing the same shading engine, not a new
 * asset) and a one-line fit summary derived from the result's own zones.
 */
export function FitMatchCard({ audience, result, selected, onSelect, isBestMatch }: Props) {
  const summary = summarizeFit(result.zones);

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={`relative flex w-full items-center gap-4 rounded-2xl p-5 text-left shadow-card transition-colors ${
        selected ? 'border-2 border-brand bg-brand/5' : 'border border-stone-200 bg-white hover:border-brand/40'
      }`}
    >
      {isBestMatch && (
        <span className="absolute -top-3 right-4 inline-flex items-center gap-1.5 rounded-full bg-brand px-3 py-1 text-xs font-semibold text-ink shadow-card">
          <StarIcon />
          Best Match
        </span>
      )}

      <div className="min-w-0 flex-1">
        <p className="truncate text-base font-semibold text-ink">{result.targetBrandName}</p>
        <p className="text-xs uppercase tracking-wide text-muted">Recommended size</p>
        <p className="text-2xl font-bold text-brand leading-tight">{result.recommendedSize}</p>
        <p className="mt-2 text-xs font-semibold uppercase tracking-wide text-muted">Fit summary</p>
        <div className="flex items-center gap-2 text-sm">
          <span aria-hidden="true" className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: ZONE_COLOR[summary.zone] }} />
          <span className="text-ink/80">{summary.text}</span>
        </div>
      </div>

      <AvatarPanel audience={audience} zones={result.zones} size="small" />
      <ChevronIcon />
    </button>
  );
}
